import { onMounted, ref } from 'vue'

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(b64)
  const bytes = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i)
  return bytes
}

function csrfToken(): string {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ''
}

function vapidPublicKey(): string | null {
  return document.querySelector<HTMLMetaElement>('meta[name="vapid-public-key"]')?.content ?? null
}

export function usePushNotifications(
  subscribePath: string,
  unsubscribePath: string,
  options: { autoSubscribe?: boolean } = {},
) {
  const isSupported  = ref(false)
  const isSubscribed = ref(false)
  const isLoading    = ref(false)
  const error        = ref<string | null>(null)

  onMounted(async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return
    if (!vapidPublicKey()) return
    isSupported.value = true

    try {
      const registration = await navigator.serviceWorker.ready
      const existing = await registration.pushManager.getSubscription()
      isSubscribed.value = !!existing

      if (options.autoSubscribe && !existing && Notification.permission !== 'denied') {
        await subscribe()
      }
    } catch { /* unsupported environment */ }
  })

  async function subscribe() {
    error.value = null
    isLoading.value = true
    try {
      const permission = await Notification.requestPermission()
      if (permission !== 'granted') {
        error.value = 'Notification permission denied'
        return
      }

      const key = vapidPublicKey()!
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly:      true,
        applicationServerKey: urlBase64ToUint8Array(key).buffer as ArrayBuffer,
      })

      const json = subscription.toJSON()
      await fetch(subscribePath, {
        method:  'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken(),
        },
        body: JSON.stringify({
          endpoint:   json.endpoint,
          p256dh_key: json.keys?.p256dh,
          auth_key:   json.keys?.auth,
        }),
      })
      isSubscribed.value = true
    } catch (e) {
      error.value = 'Failed to enable push notifications'
      console.error(e)
    } finally {
      isLoading.value = false
    }
  }

  async function unsubscribe() {
    error.value = null
    isLoading.value = true
    try {
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()
      if (subscription) {
        await subscription.unsubscribe()
        await fetch(unsubscribePath, {
          method:  'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken(),
          },
          body: JSON.stringify({ endpoint: subscription.endpoint }),
        })
      }
      isSubscribed.value = false
    } catch (e) {
      error.value = 'Failed to disable push notifications'
      console.error(e)
    } finally {
      isLoading.value = false
    }
  }

  return { isSupported, isSubscribed, isLoading, error, subscribe, unsubscribe }
}
